import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { X, Loader2, Edit,} from "lucide-react";
import MultiSelectDropdown from "../Common/MultiSelectDropdown";
import { useSuperAdmin } from "../../context/SuperAdminContext";


const COLOR = "#3B82F6";

// ── Normalizers ────────────────────────────────────────────────────────────────
const normalizeLocation = (loc) => ({ _id: loc._id || loc.id, name: loc.name });
const normalizeCompany = (comp) => ({ _id: comp._id || comp.id, name: comp.name , companyCode:comp.companyCode });
const normalizeSkill = (s) => ({ id: String(s._id || s.id), name: s.name });
const normalizeMasterDepartment = (d) => ({
  id: String(d._id || d.id),
  name: d.name || d.departmentName || d.department || "",
});

const normalizeDepartment = (dept) => ({
  id: dept._id || dept.id,
  rolename: dept.department || dept.departmentName || dept.rolename || dept.name || "",
  skillNames: (dept.skills || []).filter(s => typeof s === "string"),
  skillIds: dept.skillIds || [],
  masterDepartmentId: dept.masterDepartmentId || dept.masterDepartment?._id || dept.masterDepartment?.id || "",
  masterDepartmentName: dept.masterDepartmentName || dept.masterDepartment?.name || "",
  status: dept.isActive !== undefined ? dept.isActive : true,
});


const EMPTY_FORM = {
  departmentName: "",
  masterDepartmentId: "",
  skillIds: [],
  isActive: true,
};

// ── Chevron SVG ────────────────────────────────────────────────────────────────
const ChevronSVG = ({ open }) => (
  <svg
    className={`h-4 w-4 transform transition-transform ${open ? "rotate-180" : ""}`}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
  >
    <path
      fillRule="evenodd"
      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
      clipRule="evenodd"
    />
  </svg>
);


// ── Department Modal (Add / Edit) ──────────────────────────────────────────────
function DepartmentModal({ mode, initial, onClose, onSave, companyId }) 
{
  const {
    fetchAllSkills,
    add_master_subdepartment,
    edit_master_subdepartment,
    list_master_subdepartment,
    list_master_department,
  } = useSuperAdmin();
  const [form, setForm] = useState(
    initial
      ? {
        departmentName: initial.rolename || "",
        masterDepartmentId: initial.masterDepartmentId || "",
        skillIds: initial.skillIds || [],
        _skillNames: initial.skillNames || [],
        isActive: initial.status !== undefined ? initial.status : true,
      }
      : EMPTY_FORM
  );
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const [skills, setSkills] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [skillsLoading, setSkillsLoading] = useState(true);
  const [shiftsLoading, setShiftsLoading] = useState(true);

  const [masterDepartments, setMasterDepartments] = useState([]);
  const [masterDeptLoading, setMasterDeptLoading] = useState(true);

  // Once skills load, resolve names → IDs for preselection (only if we don't already have skillIds)
  useEffect(() => {
    if (!skillsLoading && skills.length > 0 && form._skillNames?.length > 0 && (!form.skillIds || form.skillIds.length === 0)) {
      const matched = skills
        .filter(s => form._skillNames.includes(s.name))
        .map(s => s.id);
      setForm(p => ({ ...p, skillIds: matched, _skillNames: [] }));
    }
  }, [skillsLoading, skills]);

  // Once shifts load, resolve names → IDs for preselection  
  useEffect(() => {
    if (!shiftsLoading && shifts.length > 0 && form._shiftNames?.length > 0) {
      const matched = shifts
        .filter(s => form._shiftNames.includes(s.name))
        .map(s => s.id);
      setForm(p => ({ ...p, shiftIds: matched, _shiftNames: [] }));
    }
  }, [shiftsLoading, shifts]);

  // Fetch skills
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetchAllSkills(true);
        if (!cancelled) {
          if (res?.data?.success) {
            setSkills((Array.isArray(res?.data?.skills) ? res?.data?.skills : []).map(normalizeSkill));
          } else {
            toast.error(res?.data?.message || "Failed to load skills");
          }
        }
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.message || "Failed to load skills");
      } finally {
        if (!cancelled) setSkillsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Fetch master departments (active only)
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await list_master_department(true);
        if (!cancelled) {
          if (res?.data?.success) {
            setMasterDepartments(
              (Array.isArray(res?.data?.data) ? res.data.data : []).map(normalizeMasterDepartment)
            );
          } else {
            toast.error(res?.data?.message || "Failed to load master departments");
          }
        }
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.message || "Failed to load master departments");
      } finally {
        if (!cancelled) setMasterDeptLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const set = (key, val) => {
    setForm((p) => ({ ...p, [key]: val }));
    setErrors((p) => ({ ...p, [key]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!String(form.departmentName || "").trim()) e.departmentName = "Master SubDepartment name is required";
    if (!form.masterDepartmentId) e.masterDepartmentId = "Select a master department";
    if (!form.skillIds.length) e.skillIds = "Select at least one skill";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res =
        mode === "add"
          ? await add_master_subdepartment(form.departmentName, form.masterDepartmentId, form.skillIds, form.isActive)

          : await edit_master_subdepartment(form.departmentName, form.masterDepartmentId, form.skillIds, form.isActive, initial.id);
      if (res?.data?.success) {
        toast.success(
          mode === "add" ? "SubDepartment added successfully" : "SubDepartment updated successfully"
        );
        onSave?.(normalizeDepartment(res?.data?.data || {}));
        onClose();
      } else {
        toast.error(res?.data?.message || "Failed to save");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-gray-900">
            {mode === "add" ? "Add Master SubDepartment" : "Edit Master SubDepartment"}
          </h3>
          <button onClick={onClose} disabled={submitting} className="p-1.5 rounded-md hover:bg-gray-100">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Department Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Master SubDepartment Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.departmentName}
              onChange={(e) => set("departmentName", e.target.value)}
              placeholder="e.g. Scanning"
              className={`w-full h-7 px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 ${errors.departmentName ? "border-red-400 bg-red-50" : "border-gray-300"
                }`}
            />
            {errors.departmentName && (
              <p className="text-red-500 text-xs mt-1">{errors.departmentName}</p>
            )}
          </div>

          {/* Master Department */}
        
<div>
  <label className="block text-sm font-medium text-gray-700 mb-1">
    Master Department <span className="text-red-500">*</span>
  </label>
  <select
    value={form.masterDepartmentId || ""}
    onChange={(e) => set("masterDepartmentId", e.target.value)}
    disabled={masterDeptLoading}
    className={`w-full h-8 px-3 text-sm border rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-60 ${
      errors.masterDepartmentId ? "border-red-400 bg-red-50" : "border-gray-300"
    }`}
  >
    <option value="">
      {masterDeptLoading ? "Loading..." : "Select master department"}
    </option>
    {masterDepartments.map((d) => (
      <option key={d.id} value={d.id}>
        {d.name}
      </option>
    ))}
  </select>
  {errors.masterDepartmentId && (
    <p className="text-red-500 text-xs mt-1">{errors.masterDepartmentId}</p>
  )}
</div>


          {/* Skills */}
          <MultiSelectDropdown
            label="Skills"
            options={skills}
            selected={form.skillIds}
            onChange={(val) => set("skillIds", val)}
            error={errors.skillIds}
            placeholder="Select skills"
            loading={skillsLoading}
          />


          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => set("isActive", true)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-all h-7 ${form.isActive
                    ? "bg-green-100 border-green-400 text-green-700"
                    : "bg-white border-gray-300 text-gray-400 hover:border-green-300"
                  }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${form.isActive ? "bg-green-600" : "bg-gray-300"}`} />
                Active
              </button>
              <button
                type="button"
                onClick={() => set("isActive", false)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border transition-all h-7 ${!form.isActive
                    ? "bg-red-100 border-red-400 text-red-700"
                    : "bg-white border-gray-300 text-gray-400 hover:border-red-300"
                  }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${!form.isActive ? "bg-red-600" : "bg-gray-300"}`} />
                Inactive
              </button>
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 text-white text-sm font-semibold rounded-lg hover:opacity-90 bg-blue-500 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {submitting && <Loader2 size={14} className="animate-spin" />}
            {mode === "add" ? "Add Department" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function ManageMasterSubDepartment() {

    const {list_master_subdepartment}= useSuperAdmin();
  // ── Data
  const [locations, setLocations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);

  // ── Selection
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");

  // ── Loading
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [togglingDeptId, setTogglingDeptId] = useState(null);

  // ── Dropdown open
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [isCompanyOpen, setIsCompanyOpen] = useState(false);

  // ── Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [editDept, setEditDept] = useState(null);
  const [wageHistoryDept, setWageHistoryDept] = useState(null);



  const loadDepartments = useCallback(async () => {
    try {
      setDepartmentsLoading(true);
      const res = await list_master_subdepartment(false);
      if (res?.data?.success && Array.isArray(res?.data?.data)) {
        setDepartments(res.data.data.map(normalizeDepartment));
      } else {
        toast.error(res?.data?.message || "Failed to load departments");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load departments");
    } finally {
      setDepartmentsLoading(false);
    }
  }, []);


  useEffect(() => {
    loadDepartments();
  }, [loadDepartments]);

    
  const handleEdit = useCallback(() => {
   loadDepartments();
  }, [selectedCompany, loadDepartments]);





  const locationName = selectedLocation
    ? locations.find((l) => l._id === selectedLocation)?.name || "Select Location"
    : "Select Location";

const companyName = selectedCompany
  ? (() => {
      const comp = companies.find((c) => c._id === selectedCompany);
      return comp
        ? `${comp.name}${comp.companyCode ? ` (${comp.companyCode})` : ""}`
        : "Select Company";
    })()
  : "Select Company";

  return (
    <div className="p-6" style={{ fontFamily: "'Segoe UI', sans-serif" }}>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Manage Master SubDepartments</h2>
          <p className="text-s text-gray-400">
            {departmentsLoading ? "Loading..." : `Total SubDepartments : ${departments.length}`}
          </p>
        </div>
        
          <button
            onClick={() => setShowAddModal(true)}
            className="px-4 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 flex items-center justify-center gap-1"
          >
            Add Master SubDepartment
          </button>
    
      </div>


      {/* Table */}
      <div className="w-full">
        <div className="max-h-[70vh] overflow-y-auto overflow-x-auto relative">
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 bg-white shadow-md z-[50]">
              <tr className="bg-gray-100">
                <th className="p-2 text-left">Master SubDepartment Name</th>
                <th className="p-2 text-center">Master Department</th>
                <th className="p-2 text-center">Status</th>
                <th className="p-2 text-center">Edit</th>

              </tr>
            </thead>
            <tbody>
              {departmentsLoading ? (
                <tr>
                  <td colSpan={5} className="py-16 text-center">
                    <Loader2 size={28} className="animate-spin mx-auto mb-3 text-gray-400" />
                    <p className="text-sm text-gray-400">Loading Subdepartments...</p>
                  </td>
                </tr>
              ) : departments.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-16 text-center">
                    <p className="text-sm text-gray-500 font-medium">No Master Subdepartments found.</p>
                  </td>
                </tr>
              ) : (
                departments.map((row) => (
                  <tr key={row.id} className="border-b hover:bg-gray-50">
                    <td className={`p-3 font-medium ${row.status ? "text-gray-800" : "text-gray-400"}`}>{row.rolename}</td>
                    <td className="p-3 text-center text-gray-700">
                      {row.masterDepartmentName || "—"}
                    </td>
                    <td className="p-2 text-center">
                      <button
                        type="button"
                        disabled={togglingDeptId === row.id}
                        className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold w-fit mx-auto disabled:opacity-60"
                        style={{ background: row.status ? "#DCFCE7" : "#FEE2E2", color: row.status ? "#16A34A" : "#DC2626" }}
                      >
                        {togglingDeptId === row.id ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <span className="w-1.5 h-1.5 rounded-full" style={{ background: row.status ? "#16A34A" : "#DC2626" }} />
                        )}
                        {row.status ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="p-2 text-center">
                      <button
                        onClick={() => setEditDept(row)}
                        className="p-1.5 rounded-lg hover:bg-blue-50 transition-all mx-auto block"
                        title="Edit department"
                      >
                        <Edit size={14} color={COLOR} />
                      </button>
                    </td>

                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {showAddModal && (
        <DepartmentModal
          mode="add"
          companyId={selectedCompany}
          onClose={() => setShowAddModal(false)}
          onSave={() => loadDepartments()}
        />
      )}
      {editDept && (
        <DepartmentModal
          mode="edit"
          initial={editDept}
          companyId={selectedCompany}
          onClose={() => setEditDept(null)}
          onSave={() => loadDepartments()}
        />
      )}


    </div>
  );
}